import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui";
import BaseItemType from "../components/itemtypes/question/BaseItemType";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";

interface Props {
	item: BaseItemSchema;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
	toggleShowReadMore?: (options?: { scrollToHighlight?: boolean }) => void;
}

const BaseItemTypeContainer = ({
	item,
	originalItemType,
	mappedItemType,
	toggleShowReadMore,
}: Props) => {
	const { showToast } = useToast();
	const { updateItemStates, itemStates, isSummativeTest, inReviewMode } =
		useSessionStore((state) => ({
			updateItemStates: state.updateItemStates,
			itemStates: state.itemStates,
			isSummativeTest: state.isSummativeTest,
			inReviewMode: state.inReviewMode,
			blockStates: state.blockStates,
			currentBlockIndex: state.currentBlockIndex,
			sessionItems: state.sessionItems,
		}));

	const itemId = item.id!;
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse = userResponse?.[itemId];

	const itemState = itemStates?.get(itemId);
	const isAnswered = itemState?.answered;

	const selectedChoiceIds = itemState?.selectedChoiceIds;
	const correctAnswerIds =
		itemState?.correctAnswerIds ||
		item.content?.choices
			?.filter((choice) => choice.isCorrect)
			.map((choice) => choice.id);

	const crossedOutChoiceIds = itemState?.crossedOutChoiceIds;

	const correctAnswerOrders = item.response?.correctResponse?.map(
		(choice) => choice.id,
	);

	const shouldPreventSelection = (choiceId: string) => {
		const maxChoiceCount = item.response?.numChoicesRequired?.max;
		return (
			mappedItemType === "SATA" &&
			maxChoiceCount &&
			selectedChoiceIds &&
			selectedChoiceIds.length >= maxChoiceCount &&
			!selectedChoiceIds.includes(choiceId)
		);
	};

	const handleSelectChoice = (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => {
		const maxChoiceCount = item.response?.numChoicesRequired?.max;
		if (shouldPreventSelection(choiceId)) {
			showToast({
				message: `Maximum limit reached. You can only select up to ${maxChoiceCount} answers.`,
				duration: 500,
			});
			return;
		}

		updateItemStates({
			itemId,
			selectedChoiceId: choiceId,
			multiSelect:
				mappedItemType === "SATA" || mappedItemType === "EXPLORE_ACCORDION",
			crossOut: crossingOut,
			currentOrders,
		});
	};

	const handleHighlightedTextPress = () => {
		toggleShowReadMore?.({ scrollToHighlight: true });
	};

	return (
		<BaseItemType
			item={item}
			originalItemType={originalItemType}
			mappedItemType={mappedItemType}
			selectedChoiceIds={selectedChoiceIds}
			correctAnswerIds={correctAnswerIds}
			crossedOutChoiceIds={crossedOutChoiceIds}
			isAnswered={isAnswered}
			currentUserResponse={currentUserResponse}
			isSummativeTest={isSummativeTest}
			inReview={inReviewMode}
			onSelectChoice={handleSelectChoice}
			toggleShowReadMore={toggleShowReadMore}
			correctAnswerOrders={correctAnswerOrders}
			handleHighlightedTextPress={handleHighlightedTextPress}
		/>
	);
};

export default BaseItemTypeContainer;
